import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
  CircularProgress,
  Popover,
} from "@mui/material";
import {
  formatNumberWithCommas,
  formattedJson,
  getDateFromTimestamp,
  getFormattedTime,
  getTimeInHHMMSS,
} from "../../services/utils";
import {
  downloadFileSecurely,
  getSecureValidationReportPath,
} from "../../services/fileDownloadService";
import InfoIcon from "@mui/icons-material/Info";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  IconDbInfo,
  IconReRunWhite,
  IconWatchSvg,
} from "../../common/utils/icons";
import ComparisonDashboardCard from "./ComparisonDashboardCard";
import { pieData } from "../../services/constants/results";
import Incidents from "./Incidents";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import CustomAccordion from "../Molecules/Accordian/CustomAccordion";
import ResourceSupportDocuments from "../Organisms/ResourceSupportDocuments";
import { useNavigate } from "react-router-dom";
import DownloadProgressBar from "../common/DownloadProgressBar";
import { useAuth } from "../../contexts/AuthContext";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import ScoreDetailsDrawer from "../Scoring/ScoreDetailsDrawer";
import ComparisonDashboardResults from "./ComparisonDashboardResults";
import {
  brand,
  navyScale,
  blueScale,
  scssTokens,
  darkModeTokens,
  getPremiumCardSx,
} from "../../styles/Theme";
import { resolveAdhocQueryRecordCount } from "./adhocQueryCount";
import { getComparisonScore } from "../../services/scoringService";
import { IComparisonScore } from "../../types/Scoring.type";
import { getExecutionSubGroupName } from "../../utils/executionGroup";
import useExecutionSubGroupMap from "../../hooks/useExecutionSubGroupMap";

const ComparisonSectionedDashboard = ({
  dashboardData,
  currentRuleResultId,
  currentRuleIncidentData,
  isLoading,
  setIsLoading,
  setIsTriggereBtnPressed,
  setIsCommentBtnPressed,
}: any) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const [scoreDrawerOpen, setScoreDrawerOpen] = useState(false);
  const [comparisonScoreData, setComparisonScoreData] =
    useState<IComparisonScore | null>(null);

  useEffect(() => {
    if (!currentRuleResultId || currentRuleResultId <= 0) return;
    let cancelled = false;
    getComparisonScore(currentRuleResultId)
      .then((data) => {
        if (!cancelled) setComparisonScoreData(data || null);
      })
      .catch(() => {
        if (!cancelled) setComparisonScoreData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentRuleResultId]);

  const adhocRecordCount = resolveAdhocQueryRecordCount({
    adhocQueries: dashboardData?.additional_properties?.adhoc_queries_result,
    isDetailedExecutionDataAvailable:
      dashboardData?.is_detailed_execution_data_available,
    summaryTotal:
      dashboardData?.additional_properties?.total_adhoc_queries_result_records,
  });
  const executionSubGroupMap = useExecutionSubGroupMap();
  const canRuleExecution = hasPermission("rule", "execute");
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set(),
  );

  const [dbInfoAnchorEl, setDbInfoAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  const handleDbInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setDbInfoAnchorEl(event.currentTarget);
  };

  const handleDbInfoClose = () => {
    setDbInfoAnchorEl(null);
  };

  const isDbInfoPopoverOpen = Boolean(dbInfoAnchorEl);

  const isValidationExecDetails =
    dashboardData?.additional_properties?.validation_exec_details !== null &&
    dashboardData?.additional_properties?.validation_exec_details.length > 0
      ? true
      : false;
  const supportDocumentsPath =
    dashboardData?.additional_properties?.supporting_documents;

  const reqPath =
    dashboardData?.complete_execution_params?.rule_execution_request;
  const skipDuplicateRecords =
    reqPath?.request_body?.query_params?.skip_duplicate_records;
  const additionalInfoForAllResources =
    reqPath?.additional_info_for_all_resources;
  const resourceNames =
    dashboardData?.additional_properties?.validation_exec_details;
  const formattedData = reqPath?.request_body?.resource_data.map(
    (item: any, index: any) => {
      const {
        file_name,
        linked_service_code,
        connection_key,
        resource_path,
        resource_id,
        ...otherProps
      } = item || {};
      const fileNames = additionalInfoForAllResources.find(
        (additionalItem: any) => additionalItem?.resource_id === resource_id,
      )?.file_names;
      const resource_name = resourceNames.find(
        (rsItem: any) => rsItem?.resource_id === resource_id,
      )?.resource_name;
      return (
        <tr key={index}>
          <td>
            {resource_name} ({item?.resource_id})
          </td>
          <td>
            <div className="file-scroll">
              <div className="break-word w-240">
                {Array.isArray(fileNames) && fileNames.length > 0
                  ? fileNames.map((fileName: any, index: number) => (
                      <Box className="file-name-box" key={index}>
                        {fileName}
                      </Box>
                    ))
                  : "N/A"}
              </div>
            </div>
          </td>
          <td>
            {item?.linked_service_code ? item?.linked_service_code : "N/A"}
          </td>
          <td>{item?.connection_key ? item?.connection_key : "N/A"}</td>
          <td>
            <div className="break-word w-240">
              {item.resource_path
                ? item.resource_path.replace(/"/g, "")
                : "N/A"}
            </div>
          </td>
          <td>
            <span className="position-relative">
              <Tooltip
                componentsProps={{
                  tooltip: { className: "wide-tooltip w-380" },
                }}
                title={
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      margin: 0,
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    <React.Fragment>
                      <Typography color="inherit">
                        Additional Resource Info
                      </Typography>
                      <Typography>
                        {formattedJson(JSON.stringify(otherProps))}
                      </Typography>
                    </React.Fragment>
                  </pre>
                }
              >
                <InfoIcon
                  sx={{
                    position: "absolute",
                    top: "50%",
                    transform: "translateY(-50%)",
                    right: "-24px",
                    width: "16px",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            </span>
          </td>
        </tr>
      );
    },
  );

  // Add state for download progress tracking
  const [downloadProgress, setDownloadProgress] = useState<
    Map<string, { percentage: number; loaded: number; total: number }>
  >(new Map());

  const handleSecureDownload = async (filepath: string, fileName: string) => {
    if (downloadingFiles.has(filepath)) return; // Prevent duplicate downloads

    setDownloadingFiles((prev) => new Set(prev).add(filepath));
    setDownloadProgress((prev) =>
      new Map(prev).set(filepath, { percentage: 0, loaded: 0, total: 0 }),
    );

    try {
      await downloadFileSecurely(
        filepath,
        fileName,
        true, // isExtractAndEncodeFilePath
        (progress) => {
          // Update progress for this specific file
          setDownloadProgress((prev) => new Map(prev).set(filepath, progress));
        },
      );
    } catch (error) {
      console.error(`❌ Enhanced download failed for ${fileName}:`, error);
      // Error handling is done in the downloadFileSecurely function
    } finally {
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(filepath);
        return newSet;
      });
      setDownloadProgress((prev) => {
        const newMap = new Map(prev);
        newMap.delete(filepath);
        return newMap;
      });
    }
  };

  const getSecureReportPath = (path: string) => {
    if (path) {
      const { fileName } = getSecureValidationReportPath(path);
      return {
        downloadAction: () => handleSecureDownload(path, fileName),
        fileName,
      };
    }
    return null;
  };

  const ruleVariables =
    reqPath?.request_body?.inline_variables?.rule_variables || {};
  const resourceVariables =
    reqPath?.request_body?.inline_variables?.resource_variables || [];
  const researchQueryVariables =
    reqPath?.request_body?.inline_variables
      ?.comparison_research_query_variables || [];

  // KPI Summary Bar calculations
  const kpiMatched =
    dashboardData?.additional_properties?.total_matched_records ?? 0;
  const kpiMismatched =
    dashboardData?.additional_properties?.total_mismatched_records ?? 0;
  const kpiMissing =
    dashboardData?.additional_properties?.total_unique_missing_records ?? 0;
  // const kpiTotalRecords = kpiMatched + kpiMismatched + kpiMissing;
  const kpiTotalRecords = dashboardData?.additional_properties?.total_records;
  const kpiSuccessRate =
    kpiTotalRecords > 0
      ? ((kpiMatched / kpiTotalRecords) * 100).toFixed(1)
      : "0.0";

  // DQ-8674: KPI bar — fully mode-aware colors for dark/light contrast
  // Dark: backgrounds use navyScale surfaces; danger text lightened to #ef6e6e
  // (mirrors --error in _variables.scss dark); navy text swapped to brand.ice
  const kpiBorderColor = isDark
    ? darkModeTokens.border // ice translucent on dark bg
    : scssTokens.inputBoxBorder; // #d3d3d7 on light bg

  const kpiCards = [
    {
      label: "Total Records",
      value: formatNumberWithCommas(kpiTotalRecords),
      color: brand.blue, // #689ec8 — readable on both modes
      bg: isDark ? darkModeTokens.surface : blueScale[50], // #1c1f2d | #e6f6ff
    },
    {
      label: "Matched",
      value: formatNumberWithCommas(kpiMatched),
      color: brand.teal, // #70b5a7 — sufficient contrast on both
      bg: isDark ? "rgba(112,181,167,0.18)" : "rgba(112,181,167,0.12)",
    },
    {
      label: "Missing",
      value: formatNumberWithCommas(kpiMissing),
      // Dark: lighten danger red to #ef6e6e (--error dark token) for contrast
      color: isDark ? "#ef6e6e" : scssTokens.badgeDangerBg,
      bg: isDark ? "rgba(232,67,54,0.18)" : "rgba(232,67,54,0.10)",
    },
    {
      label: "Mismatched",
      value: formatNumberWithCommas(kpiMismatched),
      color: isDark ? "#ef6e6e" : scssTokens.badgeDangerBg,
      bg: isDark ? "rgba(232,67,54,0.12)" : "rgba(232,67,54,0.06)",
    },
    {
      label: "Adhoc Query",
      value:
        adhocRecordCount === null
          ? "—"
          : formatNumberWithCommas(adhocRecordCount),
      color: brand.teal,
      bg: isDark ? "rgba(112,181,167,0.18)" : "rgba(112,181,167,0.12)",
    },
    {
      label: "Success Rate",
      value: `${kpiSuccessRate}%`,
      // brand.navy (#232739) is invisible on dark bg — swap to brand.ice (#d7f1ff)
      color: isDark ? brand.ice : brand.navy,
      bg: isDark ? darkModeTokens.surface : navyScale[100], // #1c1f2d | #d3d3d7
    },
  ];

  return (
    <>
      <Box className="dashboard-title-group">
        <Stack
          direction="row"
          spacing={2.5}
          alignItems="center"
          flexWrap="wrap"
          divider={
            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.5, borderColor: "divider" }}
            />
          }
          sx={{ width: "100%", rowGap: 1.5 }}
        >
          {/* CLUSTER 1 — Identity + Context */}
          <Box
            className="heading"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              flex: "1 1 auto",
              minWidth: 0,
            }}
          >
            <Tooltip
              title={reqPath?.request_body?.execution_name || ""}
              placement="top"
              arrow
            >
              <Typography
                variant="h6"
                noWrap
                sx={{ fontWeight: 700, lineHeight: 1.3 }}
              >
                {reqPath?.request_body?.execution_name}
              </Typography>
            </Tooltip>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              divider={
                <Box
                  component="span"
                  sx={{ color: "text.disabled", lineHeight: 1 }}
                >
                  ·
                </Box>
              }
              sx={{ color: "text.secondary", rowGap: 0.25 }}
            >
              {(dashboardData?.run_name || dashboardData?.run_id) && (
                <Typography variant="body2" color="inherit">
                  Execution Group: {dashboardData?.run_name ?? "—"}
                  {dashboardData?.run_id ? ` (${dashboardData?.run_id})` : ""}
                </Typography>
              )}
              {getExecutionSubGroupName(
                dashboardData,
                executionSubGroupMap,
              ) && (
                <Typography variant="body2" color="inherit">
                  Execution Sub Group:{" "}
                  {getExecutionSubGroupName(
                    dashboardData,
                    executionSubGroupMap,
                  )}
                </Typography>
              )}
              {dashboardData?.execution_time && (
                <Typography variant="body2" color="inherit">
                  {getDateFromTimestamp(dashboardData?.execution_time)}{" "}
                  {getFormattedTime(dashboardData?.execution_time)}
                </Typography>
              )}
              {dashboardData?.additional_properties?.total_time != null && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "inherit",
                  }}
                >
                  <IconWatchSvg />
                  <Typography variant="body2" color="inherit">
                    {getTimeInHHMMSS(
                      dashboardData?.additional_properties?.total_time,
                    )}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
          {dashboardData &&
            dashboardData?.additional_properties &&
            !dashboardData?.additional_properties?.are_all_errors_stored && (
              <div className="popover-box">
                <div
                  className="db-info-icon"
                  onClick={handleDbInfoClick}
                  style={{}}
                >
                  <IconDbInfo />
                </div>
                <Popover
                  open={isDbInfoPopoverOpen}
                  anchorEl={dbInfoAnchorEl}
                  onClose={handleDbInfoClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  slotProps={{
                    paper: {
                      className: "db-info-popover",
                    },
                  }}
                >
                  <Box className="db-error-popover-body">
                    <div className="title">Records summary</div>

                    <ul>
                      <li>
                        <div className="item-parent">
                          <div className="item1">Total mismatched : </div>
                          <div className="item2">
                            Generated &nbsp; - &nbsp;
                            {formatNumberWithCommas(
                              dashboardData?.additional_properties
                                ?.total_mismatched_records_for_all_columns || 0,
                            )}
                          </div>

                          <div className="item3">
                            Recorded &nbsp; - &nbsp;
                            {formatNumberWithCommas(
                              dashboardData?.additional_properties
                                ?.total_mismatched_records_stored_in_db || 0,
                            )}
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="item-parent">
                          <div className="item1">Total missing : </div>
                          <div className="item2">
                            Generated &nbsp; - &nbsp;
                            {formatNumberWithCommas(
                              dashboardData?.additional_properties
                                ?.total_unique_missing_records || 0,
                            )}
                          </div>
                          <div className="item3">
                            Recorded &nbsp; - &nbsp;
                            {formatNumberWithCommas(
                              dashboardData?.additional_properties
                                ?.total_missing_records_stored_in_db || 0,
                            )}
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="item-parent">
                          <div className="item1">
                            Total adhoc queries results :
                          </div>
                          <div className="item2">
                            Generated &nbsp; - &nbsp;
                            {formatNumberWithCommas(
                              dashboardData?.additional_properties
                                ?.total_adhoc_queries_result_records || 0,
                            )}
                          </div>
                          <div className="item3">
                            Recorded &nbsp; - &nbsp;
                            {formatNumberWithCommas(
                              dashboardData?.additional_properties
                                ?.total_adhoc_queries_result_records_stored_in_db ||
                                0,
                            )}
                          </div>
                        </div>
                      </li>
                    </ul>
                  </Box>
                </Popover>
              </div>
            )}
          {/* CLUSTER 3 — Result (Status) */}
          {dashboardData && (
            <Stack direction="row" spacing={1} alignItems="center">
              <label
                className={`badges badge-base ${
                  dashboardData?.job_status === "completed"
                    ? "badge-success"
                    : "badge-error"
                }`}
              >
                {dashboardData?.job_status === "completed"
                  ? "Completed"
                  : "Completed With Errors"}
              </label>
            </Stack>
          )}

          {/* CLUSTER 4 — Actions (Re-run) */}
          {canRuleExecution && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                className="btn-orange btn-dark btn-sm plus-btn-sm"
                onClick={() =>
                  navigate(
                    `/rules/${dashboardData?.domain_id}/re-run-rule-execution/${dashboardData?.rule_id}?re-run=true&execution-id=${dashboardData?.id}&run-id=${dashboardData?.run_id}&run-name=${dashboardData?.run_name}&execution-sub-group-id=${dashboardData?.execution_sub_group_id}&execution-sub-group-code=${dashboardData?.execution_sub_group_code}`,
                  )
                }
              >
                <IconReRunWhite /> &nbsp; ReRun
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>
      <ScoreDetailsDrawer
        open={scoreDrawerOpen}
        onClose={() => setScoreDrawerOpen(false)}
        type="comparison"
        executionId={currentRuleResultId}
      />

      {/* KPI Summary Bar */}
      {dashboardData && (
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            mb: 2,
            border: `1px solid ${kpiBorderColor}`,
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {kpiCards.map((kpi, idx) => (
            <Box
              key={kpi.label}
              sx={{
                flex: "1 1 120px",
                px: 3,
                py: 2,
                textAlign: "center",
                borderRight:
                  idx < kpiCards.length - 1
                    ? `1px solid ${kpiBorderColor}`
                    : "none",
                borderTop: `3px solid ${kpi.color}`,
                backgroundColor: kpi.bg,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  {kpi.label}
                </Typography>
                {kpi.label === "Success Rate" && (
                  <Tooltip title="View DQ Score details" placement="top" arrow>
                    <Box
                      component="span"
                      role="button"
                      tabIndex={0}
                      aria-label="View DQ Score details"
                      onClick={() => setScoreDrawerOpen(true)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setScoreDrawerOpen(true);
                        }
                      }}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        color: "text.secondary",
                        transition: "color .2s ease",
                        "&:hover": { color: "primary.main" },
                        "&:focus-visible": {
                          outline: "2px solid",
                          outlineColor: "primary.main",
                          outlineOffset: 2,
                          borderRadius: "2px",
                        },
                      }}
                    >
                      <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
                    </Box>
                  </Tooltip>
                )}
              </Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: kpi.color, lineHeight: 1.2 }}
              >
                {kpi.value}
              </Typography>
            </Box>
          ))}
        </Paper>
      )}

      {/* Dataset Summary Section */}
      <Paper
        className="accordion-panel"
        elevation={0}
        sx={getPremiumCardSx(mode)}
      >
        <Accordion defaultExpanded className="heading-bold">
          <AccordionSummary
            aria-controls="dataset-summary-content"
            id="dataset-summary-header"
            expandIcon={<GridExpandMoreIcon />}
          >
            <h4>Dataset Summary</h4>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container rowSpacing={2} columnSpacing={2} marginBottom="8px">
              {isValidationExecDetails ? (
                dashboardData?.additional_properties?.validation_exec_details?.map(
                  (item: any, index: any) => {
                    let duplicateRecords =
                      item?.validation_result?.additional_properties
                        ?.total_duplicate_records || 0;
                    const localPieData = { ...pieData };
                    localPieData.resource_name = item.resource_name;
                    localPieData.resource_id = item.resource_id;
                    localPieData.domain_id = item?.validation_result?.domain_id;
                    localPieData.total_records = item.total_records_count;
                    localPieData.total_common_records =
                      item.total_common_records;
                    localPieData.total_mismatched_records =
                      item.total_mismatched_records;
                    localPieData.total_missing_records =
                      item.missing_records_count;
                    localPieData.total_duplicate_records = duplicateRecords;
                    localPieData.total_unique_records = item.unique_records;
                    localPieData.filtered_records_by_resource_filters =
                      item?.validation_result?.additional_properties
                        ?.filtered_records_by_resource_filters || 0;
                    localPieData.total_null_key_records =
                      item?.validation_result?.additional_properties
                        ?.filtered_records_by_null_values_in_unique_keys || 0;
                    localPieData.total_matched_records =
                      dashboardData?.additional_properties
                        ?.total_common_records -
                      dashboardData?.additional_properties
                        ?.total_mismatched_records;
                    localPieData.false_positive_missing_records_count =
                      item?.false_positive_missing_records_count || 0;
                    localPieData.filtered_records_by_rule_filters =
                      item.filtered_records_by_rule_filters || 0;
                    localPieData.countsData = {
                      match:
                        dashboardData?.additional_properties
                          ?.total_matched_records,
                      mismatch:
                        dashboardData?.additional_properties
                          ?.total_mismatched_records,
                      missing: item.missing_records_count,
                      duplicate: duplicateRecords,
                      total: item.total_records_count || 0,
                    };
                    localPieData.key = { index };
                    localPieData.are_all_errors_stored =
                      item?.validation_result?.additional_properties?.are_all_errors_stored;
                    localPieData.total_validation_errors =
                      item?.validation_result?.additional_properties
                        ?.total_validation_errors || 0;
                    localPieData.total_validation_errors_stored_in_db =
                      item?.validation_result?.additional_properties
                        ?.total_validation_errors_stored_in_db || 0;
                    localPieData.total_duplicate_records_stored_in_db =
                      item?.validation_result?.additional_properties
                        ?.total_duplicate_records_stored_in_db || 0;
                    localPieData.filtered_records_by_null_values_in_unique_keys =
                      item?.validation_result?.additional_properties
                        ?.filtered_records_by_null_values_in_unique_keys || 0;
                    localPieData.filtered_records_by_null_values_in_unique_keys_stored_in_db =
                      item?.validation_result?.additional_properties
                        ?.filtered_records_by_null_values_in_unique_keys_stored_in_db ||
                      0;
                    localPieData.total_cross_field_validation_results =
                      item?.validation_result?.additional_properties
                        ?.total_cross_field_validation_results || 0;
                    localPieData.total_cross_field_validation_results_stored_in_db =
                      item?.validation_result?.additional_properties
                        ?.total_cross_field_validation_results_stored_in_db ||
                      0;
                    return (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={4}
                        lg={4}
                        xl={4}
                        key={index}
                      >
                        <ComparisonDashboardCard
                          pieData={localPieData}
                          domainId={dashboardData?.domain_id}
                          skipDuplicateRecords={skipDuplicateRecords}
                          totalMatchRecords={
                            dashboardData?.additional_properties
                              ?.total_matched_records
                          }
                        />
                      </Grid>
                    );
                  },
                )
              ) : (
                <Grid item xs={12} sm={12} md={4} lg={4}>
                  <ComparisonDashboardCard
                    pieData={pieData}
                    totalMatchRecords={
                      dashboardData?.additional_properties
                        ?.total_matched_records
                    }
                  />
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Paper>
      <Incidents
        currentResultId={currentRuleResultId || ""}
        currentIncidentData={currentRuleIncidentData ?? []}
        setIsLoading={setIsLoading}
        setIsTriggereBtnPressed={setIsTriggereBtnPressed}
        setIsCommentBtnPressed={setIsCommentBtnPressed}
      />
      <Paper
        className="accordion-panel"
        elevation={0}
        sx={getPremiumCardSx(mode)}
      >
        <ComparisonDashboardResults
          dashboardData={dashboardData}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
      </Paper>
      {reqPath?.request_body && (
        <>
          <Paper
            className="accordion-panel"
            elevation={0}
            sx={getPremiumCardSx(mode)}
          >
            <Accordion
              className="heading-bold"
              sx={{ my: 0, "&.Mui-expanded": { margin: 0 } }}
            >
              <AccordionSummary
                aria-controls="panel2d-content"
                id="panel2d-header"
                expandIcon={<GridExpandMoreIcon />}
              >
                <h4>Supporting Documents </h4>
              </AccordionSummary>
              <AccordionDetails sx={{ paddingTop: "16px" }}>
                <CustomAccordion
                  expandId="panel2d-header-support-documents"
                  title="Rule Execution Report"
                  isEnabled={true}
                  topMargin={1}
                >
                  <table className="custom-table">
                    <tbody>
                      <tr>
                        <th className="w-180">Report Type </th>
                        <th>Report Location </th>
                      </tr>

                      <tr>
                        <td width="180">Rule Execution Report</td>
                        <td>
                          {dashboardData?.additional_properties
                            ?.supporting_documents?.rule_execution_report
                            ? (() => {
                                const reportPath = getSecureReportPath(
                                  supportDocumentsPath?.rule_execution_report,
                                );
                                const isDownloading = downloadingFiles.has(
                                  supportDocumentsPath?.rule_execution_report,
                                );

                                return (
                                  <Box className="word-break-all row-hr">
                                    <Box
                                      sx={{
                                        color: "text.primary",
                                        cursor: isDownloading
                                          ? "not-allowed"
                                          : "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                      onClick={() =>
                                        !isDownloading &&
                                        reportPath?.downloadAction()
                                      }
                                    >
                                      {isDownloading && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <CircularProgress size={16} />
                                          {downloadProgress.has(
                                            supportDocumentsPath?.rule_execution_report,
                                          ) && (
                                            <Typography
                                              variant="caption"
                                              sx={{ minWidth: 50 }}
                                            >
                                              {downloadProgress.get(
                                                supportDocumentsPath?.rule_execution_report,
                                              )?.percentage || 0}
                                              %
                                            </Typography>
                                          )}
                                        </Box>
                                      )}
                                      {reportPath?.fileName}
                                      {isDownloading &&
                                        downloadProgress.has(
                                          supportDocumentsPath?.rule_execution_report,
                                        ) && (
                                          <Box sx={{ width: "100%", mt: 1 }}>
                                            <DownloadProgressBar
                                              value={
                                                downloadProgress.get(
                                                  supportDocumentsPath?.rule_execution_report,
                                                )?.percentage || 0
                                              }
                                            />
                                          </Box>
                                        )}
                                    </Box>
                                  </Box>
                                );
                              })()
                            : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td width="180">
                          Comparison Research Query Result Reports
                        </td>
                        <td>
                          {supportDocumentsPath
                            ?.comparison_research_query_result_reports?.length >
                          0
                            ? supportDocumentsPath.comparison_research_query_result_reports.map(
                                (researchItem: any) => {
                                  const reportPath =
                                    getSecureReportPath(researchItem);
                                  const isDownloading =
                                    downloadingFiles.has(researchItem);

                                  return (
                                    <Box
                                      key={researchItem}
                                      className="word-break-all row-hr"
                                    >
                                      <Box
                                        sx={{
                                          color: "text.primary",
                                          cursor: isDownloading
                                            ? "not-allowed"
                                            : "pointer",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                        onClick={() =>
                                          !isDownloading &&
                                          reportPath?.downloadAction()
                                        }
                                      >
                                        {isDownloading && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <CircularProgress size={16} />
                                            {downloadProgress.has(
                                              researchItem,
                                            ) && (
                                              <Typography
                                                variant="caption"
                                                sx={{ minWidth: 50 }}
                                              >
                                                {downloadProgress.get(
                                                  researchItem,
                                                )?.percentage || 0}
                                                %
                                              </Typography>
                                            )}
                                          </Box>
                                        )}
                                        <Box
                                          sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            width: "100%",
                                          }}
                                        >
                                          {reportPath?.fileName}
                                          {isDownloading &&
                                            downloadProgress.has(
                                              researchItem,
                                            ) && (
                                              <Box
                                                sx={{ width: "100%", mt: 1 }}
                                              >
                                                <DownloadProgressBar
                                                  value={
                                                    downloadProgress.get(
                                                      researchItem,
                                                    )?.percentage || 0
                                                  }
                                                />
                                              </Box>
                                            )}
                                        </Box>
                                      </Box>
                                    </Box>
                                  );
                                },
                              )
                            : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td>Filter Records Report</td>
                        <td>
                          {supportDocumentsPath?.filter_records_report !=
                            null &&
                          supportDocumentsPath?.filter_records_report.length > 0
                            ? supportDocumentsPath?.filter_records_report.map(
                                (filterItem: string, index: number) => {
                                  const reportPath =
                                    getSecureReportPath(filterItem);
                                  const isDownloading =
                                    downloadingFiles.has(filterItem);

                                  return (
                                    <Box
                                      key={index}
                                      className="word-break-all row-hr"
                                    >
                                      <Box
                                        sx={{
                                          color: "text.primary",
                                          cursor: isDownloading
                                            ? "not-allowed"
                                            : "pointer",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                        }}
                                        onClick={() =>
                                          !isDownloading &&
                                          reportPath?.downloadAction()
                                        }
                                      >
                                        {isDownloading && (
                                          <CircularProgress size={16} />
                                        )}
                                        {reportPath?.fileName}
                                      </Box>
                                    </Box>
                                  );
                                },
                              )
                            : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td>Adhoc Query Reports</td>
                        <td>
                          {supportDocumentsPath?.adhoc_query_result_reports !=
                          null
                            ? supportDocumentsPath.adhoc_query_result_reports.filter(
                                (filterItem: any) => filterItem != null,
                              ).length > 0
                              ? supportDocumentsPath.adhoc_query_result_reports
                                  .filter(
                                    (filterItem: any) => filterItem != null,
                                  ) // Filter again for rendering
                                  .map((filterItem: string, index: number) => {
                                    const reportPath =
                                      getSecureReportPath(filterItem);
                                    const isDownloading =
                                      downloadingFiles.has(filterItem);

                                    return (
                                      <Box
                                        key={index}
                                        className="word-break-all row-hr"
                                      >
                                        <Box
                                          sx={{
                                            color: "text.primary",
                                            cursor: isDownloading
                                              ? "not-allowed"
                                              : "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                          onClick={() =>
                                            !isDownloading &&
                                            reportPath?.downloadAction()
                                          }
                                        >
                                          {isDownloading && (
                                            <CircularProgress size={16} />
                                          )}
                                          {reportPath?.fileName}
                                        </Box>
                                      </Box>
                                    );
                                  })
                              : "N/A"
                            : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <td width="180">Output Logs</td>
                        <td>
                          {dashboardData?.additional_properties
                            ?.supporting_documents?.output_logs
                            ? (() => {
                                const reportPath = getSecureReportPath(
                                  supportDocumentsPath?.output_logs,
                                );
                                const isDownloading = downloadingFiles.has(
                                  supportDocumentsPath?.output_logs,
                                );

                                return (
                                  <Box className="word-break-all row-hr">
                                    <Box
                                      sx={{
                                        color: "text.primary",
                                        cursor: isDownloading
                                          ? "not-allowed"
                                          : "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                      onClick={() =>
                                        !isDownloading &&
                                        reportPath?.downloadAction()
                                      }
                                    >
                                      {isDownloading && (
                                        <CircularProgress size={16} />
                                      )}
                                      {reportPath?.fileName}
                                    </Box>
                                  </Box>
                                );
                              })()
                            : "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </CustomAccordion>

                {isValidationExecDetails &&
                  dashboardData?.additional_properties?.validation_exec_details?.map(
                    (item: any, index: any) => {
                      const supportingDocuments =
                        item?.validation_result?.additional_properties
                          ?.supporting_documents;
                      return (
                        supportingDocuments && (
                          <ResourceSupportDocuments
                            key={index}
                            title={`${item?.resource_name}(${item?.resource_id})`}
                            documents={supportingDocuments}
                          />
                        )
                      );
                    },
                  )}
              </AccordionDetails>
            </Accordion>
          </Paper>
          <Paper
            className="accordion-panel"
            elevation={0}
            sx={{ ...getPremiumCardSx(mode), mt: 1 }}
          >
            <Accordion className="heading-bold">
              <AccordionSummary
                aria-controls="panel2d-content"
                id="panel2d-header"
                expandIcon={<GridExpandMoreIcon />}
              >
                <h4>Comparison Parameters </h4>
              </AccordionSummary>
              <AccordionDetails sx={{ paddingTop: "16px" }}>
                <table className="custom-table">
                  <tbody>
                    <tr>
                      <th>Resource Name (Resource ID)</th>
                      <th>File Name</th>
                      <th>Connection Type Code</th>
                      <th>Connection Key</th>
                      <th>Resource Location</th>
                      <th>Additional Resource Info</th>
                    </tr>
                    {formattedData}
                  </tbody>
                </table>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </>
      )}
      {((dashboardData &&
        reqPath?.request_body &&
        reqPath?.request_body?.inline_variables &&
        Object.keys(ruleVariables).length > 0) ||
        resourceVariables.length > 0 ||
        Object.keys(researchQueryVariables).length > 0) && (
        <Paper
          className="accordion-panel"
          elevation={0}
          sx={getPremiumCardSx(mode)}
        >
          <Accordion className="heading-bold">
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
              expandIcon={<GridExpandMoreIcon />}
            >
              Variables
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: "16px" }}>
              <Grid container spacing={2.5}>
                {resourceVariables.length > 0 && (
                  <Grid item xs>
                    <h3 className="m-0 mb-2">{"Resource Variables"}</h3>
                    {resourceVariables.map((item: any) => {
                      const resourceNames =
                        dashboardData?.additional_properties
                          ?.validation_exec_details;
                      const resourceName = resourceNames.find(
                        (rsItem: any) =>
                          rsItem?.resource_id === item?.resource_id,
                      )?.resource_name;
                      return (
                        <div
                          key={item?.resource_id}
                          className="text-box-card box-card-variables full-radius"
                        >
                          <h3>{resourceName}</h3>

                          <table className="inline-variables-table">
                            <tbody>
                              {item?.resource_vars &&
                                Object.entries(item?.resource_vars).length >
                                  0 &&
                                Object.entries(item?.resource_vars).map(
                                  ([key, value]: any) => (
                                    <tr key={`${item.resource_id}_${key}`}>
                                      <td>
                                        <div className="inner-column">
                                          <div className="label">
                                            <strong>{key}</strong>
                                            <span className="required-asterisk">
                                              *
                                            </span>
                                            :
                                          </div>
                                          <input
                                            className={`form-control-1 read-only`}
                                            value={value}
                                            name={`${key}_${item.resource_id}`}
                                            title={`inlineVariables[${key}_${item.resource_id}]`}
                                            readOnly
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  ),
                                )}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </Grid>
                )}
                {Object.keys(ruleVariables).length > 0 && (
                  <Grid item xs>
                    <h3 className="m-0 mb-2">{"Rule Variables"}</h3>

                    <table className="inline-variables-table">
                      <tbody>
                        {Object.keys(ruleVariables).map(
                          (key: string, index: number) => {
                            return (
                              <tr key={index}>
                                <td>
                                  <div className="inner-column">
                                    <div className="label">
                                      <strong>{key}</strong>
                                      <span className="required-asterisk">
                                        *
                                      </span>
                                      :
                                    </div>
                                    <input
                                      className="form-control-1 read-only"
                                      value={ruleVariables[key]}
                                      name={key}
                                      title={ruleVariables[key]}
                                      readOnly
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          },
                        )}
                      </tbody>
                    </table>
                  </Grid>
                )}

                {Object.keys(researchQueryVariables).length > 0 && (
                  <Grid item xs>
                    <h3 className="m-0 mb-2">{"Research Query Variables"}</h3>

                    <table className="inline-variables-table">
                      <tbody>
                        {Object.keys(researchQueryVariables).map(
                          (key: string, index: number) => {
                            return (
                              <tr key={index}>
                                <td>
                                  <div className="inner-column">
                                    <div className="label">
                                      <strong>{key}</strong>
                                      <span className="required-asterisk">
                                        *
                                      </span>
                                      :
                                    </div>
                                    <input
                                      className="form-control-1 read-only"
                                      value={researchQueryVariables[key]}
                                      name={key}
                                      title={researchQueryVariables[key]}
                                      readOnly
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          },
                        )}
                      </tbody>
                    </table>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Paper>
      )}
    </>
  );
};

export default ComparisonSectionedDashboard;
